/**
 * R210 — medidor da tradução de exibição que volta ao original (inglês) sozinha.
 *
 * Nada aqui muda o que a tela pinta. Cada evento é um registro estruturado SEM texto
 * da conversa: só um carimbo curto do texto (FNV-1a de 32 bits + tamanho), o motivo
 * e contadores. O QA calcula o mesmo carimbo dos nove textos conhecidos e procura
 * no registro (`window.__presentationDiag`, últimos 300 eventos, ou o console
 * `[presentation-diag]`). Nunca grava texto, nome, identificador de inquilino ou usuário.
 */

export type PresentationDiagEvent =
  | { event: 'scope_created'; cause: string[]; total?: number }
  /** R219: o apresentador foi descartado; `inFlight` = textos que ainda esperavam resposta nele. */
  | { event: 'scope_disposed'; inFlight: number; total?: number }
  | {
      event: 'unavailable'
      stage: 'presenter' | 'transport' | 'admission' | 'display'
      reason: string
      text?: string
      status?: number
    }
  | {
      event: 'reverted'
      text: string
      cachedState: string
      retained: boolean
      resultState: string
      ageMs: number
    }
  | { event: 'displayed_cache_overflow'; limit: number; evicted: number }

export const DIAG_BUFFER_SIZE = 300
const PER_KEY_LIMIT = 3
const seen = new Map<string, number>()
/**
 * R219: `scope_created`/`scope_disposed` não têm texto nem motivo, então a chave de repetição abaixo
 * os reduzia a 3 registros por sessão — o medidor do QA mostrava "3 escopos" mesmo que fossem
 * centenas. Agora a chave usa a causa e cada registro leva o total corrido.
 */
const totals = new Map<string, number>()

/** Carimbo não reversível o bastante para log: FNV-1a 32 bits + tamanho. */
export function textStamp(text: string): string {
  let hash = 0x811c9dc5
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return `${hash.toString(16).padStart(8, '0')}:${text.length}`
}

/** Campos que podem sair na linha de console. Qualquer outro é descartado (privacidade). */
const LINE_FIELDS = [
  'event',
  'stage',
  'reason',
  'text',
  'status',
  'cause',
  'total',
  'inFlight',
  'cachedState',
  'retained',
  'resultState',
  'ageMs',
  'limit',
  'evicted',
  'at',
] as const

/**
 * Serializador seguro: a linha de console é TEXTO (o canal de coleta de logs mostra
 * objeto como "Object"). Só campos da lista permitida, só primitivos (ou lista de
 * textos curtos), strings truncadas; nunca lança (circular, erro, getter que falha).
 */
export function presentationLine(record: object): string {
  try {
    const source = record as Record<string, unknown>
    const safe: Record<string, unknown> = {}
    for (const field of LINE_FIELDS) {
      const value = source[field]
      if (typeof value === 'string') safe[field] = value.slice(0, 80)
      else if (typeof value === 'number' && Number.isFinite(value)) safe[field] = value
      else if (typeof value === 'boolean') safe[field] = value
      else if (Array.isArray(value))
        safe[field] = value
          .filter((v) => typeof v === 'string')
          .slice(0, 8)
          .map((v: string) => v.slice(0, 40))
    }
    return `[presentation-diag] ${JSON.stringify(safe)}`
  } catch {
    return '[presentation-diag] {"event":"serialize_failed"}'
  }
}

type Sanitized = Omit<PresentationDiagEvent, 'text'> & { text?: string; at: number }

function diagKey(raw: { text?: string; reason?: string; event: string; cause?: string[]; inFlight?: number }): string {
  return `${raw.event}|${raw.reason ?? raw.cause?.join('+') ?? (raw.inFlight ? 'inflight' : '')}|${raw.text ?? ''}`
}

/** Registra um evento. Nunca lança; repete no máximo 3 vezes o mesmo (evento, motivo, texto). */
export function reportPresentation(entry: PresentationDiagEvent): void {
  try {
    const raw = entry as { text?: string; reason?: string; event: string; cause?: string[]; inFlight?: number }
    const counted = raw.event === 'scope_created' || raw.event === 'scope_disposed'
    const total = counted ? (totals.get(raw.event) ?? 0) + 1 : 0
    if (counted) totals.set(raw.event, total)
    const key = diagKey(raw)
    const count = (seen.get(key) ?? 0) + 1
    seen.set(key, count)
    if (count > PER_KEY_LIMIT) return
    if (seen.size > 5000) seen.clear()
    const record = {
      ...entry,
      ...(counted ? { total } : {}),
      ...(raw.text === undefined ? {} : { text: textStamp(raw.text) }),
      at: Date.now(),
    } as Sanitized
    const target = globalThis as { __presentationDiag?: Sanitized[] }
    const buffer = (target.__presentationDiag ??= [])
    buffer.push(record)
    if (buffer.length > DIAG_BUFFER_SIZE) buffer.splice(0, buffer.length - DIAG_BUFFER_SIZE)
    console.info(presentationLine(record))
  } catch {
    /* medir nunca pode quebrar a tela */
  }
}

/** Só para testes. */
export function resetPresentationDiagnosticsForTests() {
  seen.clear()
  totals.clear()
  ;(globalThis as { __presentationDiag?: unknown[] }).__presentationDiag = []
}
